/**
 * keyAvailability.js — which response options a wave actually carries.
 *
 * A tracking question's option list changes between waves. Andy Burnham and
 * Rupert Lowe joined "best Prime Minister" at wave 5 and Keir Starmer left it
 * the same wave; the local-issues battery didn't exist at wave 1; SNP and Plaid
 * only appear in the past-vote question at wave 2. The parquet mirror records
 * that faithfully — an option that was not offered is NULL for every respondent
 * in that wave, never 0 — so nothing needs to be declared per question: an
 * option is "not asked here" exactly when the rows in scope carry no value for
 * it.
 *
 * Keeping the test in one place is what makes it general. Anything that renders
 * an option list (sidebar bars and table, the drill-down option buttons, the
 * trend toggles) asks the same question of the same rows and so drops the same
 * options.
 */
import { numericOrNull } from "./keyWeights";

/**
 * Values this small count as "no data" rather than a real zero.
 *
 * NULL is the normal signal and is what the current mirror writes. The epsilon
 * covers datasets that fill a not-asked option with 0 instead: rolled up over
 * hundreds of areas, a genuinely offered option is never exactly zero.
 */
export const NO_DATA_EPSILON = 1e-6;

/**
 * The subset of `keys` that at least one row carries a value for.
 *
 * Pass every row set in scope (national baseline, constituency, ward, and the
 * unfiltered variants). The union is deliberate and is the safe direction: an
 * option can legitimately read zero in one small area, so it only counts as
 * absent when nothing in scope has a value for it.
 *
 * @param {Array<Array<object>|null|undefined>} rowSets
 * @param {Array<string>} keys
 * @param {object} [opts]
 * @param {number} [opts.epsilon]
 * @returns {Array<string>} the surviving keys, in the order given
 */
export function keysWithDataIn(rowSets, keys, opts = {}) {
  const eps = opts.epsilon ?? NO_DATA_EPSILON;
  const wanted = (keys || []).filter(Boolean);
  if (!wanted.length) return [];

  const sets = (rowSets || []).filter((rows) => Array.isArray(rows) && rows.length);
  if (!sets.length) return [];

  const found = new Set();
  for (const rows of sets) {
    // Resolve each key to a physical column once per row set rather than per
    // cell: some endpoints return prefixed column names, and re-scanning every
    // row's keys for a match is what makes a whole-batch check expensive.
    const sample = rows[0] || {};
    const names = Object.keys(sample);
    const colFor = new Map();
    for (const key of wanted) {
      if (key in sample) colFor.set(key, key);
      else {
        const alias = names.find((n) => String(n).includes(String(key)));
        if (alias) colFor.set(key, alias);
      }
    }

    for (const row of rows) {
      for (const key of wanted) {
        if (found.has(key)) continue;
        const col = colFor.get(key);
        if (!col) continue;
        const v = numericOrNull(row?.[col]);
        if (v !== null && Math.abs(v) > eps) found.add(key);
      }
      if (found.size === wanted.length) return wanted.slice();
    }
  }

  return wanted.filter((k) => found.has(k));
}

/**
 * Restrict `keys` to those the rows carry data for, but never to nothing.
 *
 * Rows arrive asynchronously, so "no key has data" nearly always means "not
 * loaded yet" rather than "this question is empty". Returning the full list
 * then keeps the option list stable while a fetch is in flight instead of
 * blanking it and refilling it a moment later.
 */
export function restrictKeysToData(rowSets, keys, opts = {}) {
  const available = keysWithDataIn(rowSets, keys, opts);
  return available.length ? available : (keys || []).filter(Boolean);
}
